/**
 * Admin › Memorial Services — approve / archive / delete requests.
 * Simple queue view with expand-to-review-details.
 */
import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw, CheckCircle2, Archive, Trash2, ExternalLink, HandHeart, MapPin,
} from "lucide-react";

const STATUSES = ["pending", "approved", "archived", "all"];

export default function AdminServices() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/services", { params: { status } });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const setSt = async (id, newStatus, note) => {
    try {
      await http.patch(`/admin/services/${id}`, { status: newStatus, admin_comment: note });
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this request permanently?")) return;
    try {
      await http.delete(`/admin/services/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-services">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">MEMORIAL SERVICES · QUEUE</div>
          <h2 className="font-display text-3xl">Service requests.</h2>
          <p className="mt-1 text-xs opacity-70 max-w-2xl">
            Approve to publish. Archive to hide without deleting. Contact
            info is auto-hidden from anonymous browsers on the public detail page.
          </p>
        </div>
        <button onClick={load} className="wofy-btn-ghost text-sm" aria-label="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-1.5">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`svc-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs rounded-full ${status === s ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={status === s ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
          >
            {s} {counts[s] !== undefined ? `(${counts[s]})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-6 text-sm opacity-60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-6 wofy-card text-center py-10">
          <HandHeart className="w-8 h-8 mx-auto opacity-40 mb-2" />
          <div className="font-display text-lg">Queue is clear.</div>
        </div>
      ) : (
        <div className="mt-6 grid gap-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`svc-row-${s.id}`} className="wofy-card">
              <div className="flex flex-wrap items-start gap-4">
                {s.cover_image ? (
                  <img src={s.cover_image} alt="" className="w-20 h-20 rounded-lg object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--wofy-surface)" }}>
                    <HandHeart className="w-6 h-6 opacity-40" />
                  </div>
                )}
                <div className="flex-1 min-w-[240px]">
                  <div className="text-[10px] font-mono uppercase opacity-70">
                    {s.category}{s.subcategory ? ` · ${s.subcategory}` : ""}
                  </div>
                  <div className="font-display text-lg mt-0.5">{s.title}</div>
                  <p className="text-xs opacity-80 mt-1 line-clamp-2">{s.short_description}</p>
                  <div className="mt-2 flex items-center gap-3 text-[11px] opacity-70 font-mono">
                    {(s.memorial_city || s.memorial_country) && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[s.memorial_city, s.memorial_country].filter(Boolean).join(", ")}
                      </span>
                    )}
                    <span>{s.requester_full_name} · {s.requester_email}</span>
                    {s.submitter_verified && <span className="text-emerald-600">✓ verified</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <a href={`/services/${s.slug}`} target="_blank" rel="noopener noreferrer" className="wofy-btn-ghost text-xs">
                    <ExternalLink className="w-3.5 h-3.5" /> Open
                  </a>
                  {s.status !== "approved" && (
                    <button onClick={() => setSt(s.id, "approved")} data-testid={`svc-approve-${s.id}`} className="wofy-btn-primary text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </button>
                  )}
                  {s.status !== "archived" && (
                    <button onClick={() => setSt(s.id, "archived")} className="wofy-btn-ghost text-xs">
                      <Archive className="w-3.5 h-3.5" /> Archive
                    </button>
                  )}
                  <button onClick={() => del(s.id)} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
